import React, { useState, useEffect } from 'react';
import { HiHome, HiInformationCircle, HiShoppingBag, HiPhotograph, HiPhone } from 'react-icons/hi';
import { MdFormatQuote } from 'react-icons/md';

const NAV_ITEMS = [
  { label: 'Home', id: 'Home', icon: <HiHome /> },
  { label: 'About Us', id: 'About', icon: <HiInformationCircle /> },
  { label: 'Products', id: 'Products', icon: <HiShoppingBag /> },
  { label: 'Gallery', id: 'Gallery', icon: <HiPhotograph /> },
  { label: 'Contact Us', id: 'Contact', icon: <HiPhone /> }
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [active, setActive] = useState('Home');

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    // Highlights the nav item based on which section is currently in view.
    const sectionByLabel = {
      Home: 'home',
      About: 'about',
      Products: 'products',
      Gallery: 'gallery',
      Contact: 'contact',
    };

    let rafId = null;
    const onScroll = () => {
      if (rafId) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = null;

        const offset = 110; // matches fixed navbar height + spacing
        const labels = Object.keys(sectionByLabel);

        const candidates = labels
          .map(label => {
            const el = document.getElementById(sectionByLabel[label]);
            if (!el) return null;
            const r = el.getBoundingClientRect();
            return { label, top: r.top, bottom: r.bottom };
          })
          .filter(Boolean);

        if (!candidates.length) return;

        // Prefer the section that spans the "active line".
        const spanning = candidates
          .filter(c => c.top - offset <= 0 && c.bottom - offset > 0)
          .sort((a, b) => a.top - b.top);

        const next = (spanning[0]?.label || candidates.sort((a, b) => Math.abs(a.top - offset) - Math.abs(b.top - offset))[0].label);
        setActive(prev => (prev === next ? prev : next));
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id.toLowerCase())?.scrollIntoView({ behavior: 'smooth' });
    setActive(id);
    setMenuOpen(false);
  };

  const S = styles(scrolled);

  return (
    <>
      <nav style={S.nav}>
        <div className="nav-inner" style={S.inner}>
          {/* Logo */}
          <div style={S.logo} onClick={() => scrollTo('Home')}>
            <div className="nav-logo-mark" style={S.logoMark}>LP</div>
            <div>
              <div className="nav-logo-name" style={S.logoName}>Laxmi Printers</div>
              <div style={S.logoSub}>HYDERABAD · EST. 2009</div>
            </div>
          </div>

          {/* Desktop Links */}
          <div className="nav-links" style={S.links}>
            {NAV_ITEMS.map(item => (
              <button key={item.id} onClick={() => scrollTo(item.id)} style={S.link(active === item.id)}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 16 }}>{item.icon}</span> {item.label}
                </span>
                {active === item.id && <span style={S.linkDot} />}
              </button>
            ))}
          </div>

          <button className="nav-cta" style={S.cta} onClick={() => scrollTo('Contact')}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <MdFormatQuote style={{ fontSize: 18 }} /> Get Free Quote
            </span>
          </button>

          {/* Burger */}
          <button className="nav-burger" style={S.burger} onClick={() => setMenuOpen(o => !o)}>
            <span style={S.burgerLine(menuOpen, 0)} />
            <span style={S.burgerLine(menuOpen, 1)} />
            <span style={S.burgerLine(menuOpen, 2)} />
          </button>
        </div>

        {/* Mobile Menu */}
        {menuOpen && (
          <div style={S.mobileMenu}>
            {NAV_ITEMS.map(item => (
              <button key={item.id} style={S.mobileLink} onClick={() => scrollTo(item.id)}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 18 }}>{item.icon}</span> {item.label}
                </span>
              </button>
            ))}
            <button style={S.mobileCta} onClick={() => scrollTo('Contact')}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}>
                <MdFormatQuote style={{ fontSize: 20 }} /> Get Free Quote
              </span>
            </button>
          </div>
        )}
      </nav>
      <style>{`
        @keyframes slideDown { from{opacity:0;transform:translateY(-8px)} to{opacity:1;transform:translateY(0)} }
        .nav-links { display: flex; }
        @media(max-width:768px){
          .nav-links { display:none !important; }
          .nav-cta { display:none !important; }
          .nav-burger { display:flex !important; }
        }
        @media(max-width:600px){
          .nav-inner { padding:0 20px !important; height:64px !important; }
          .nav-logo-name { font-size:16px !important; }
          .nav-logo-mark { width:36px !important; height:36px !important; font-size:15px !important; }
          .nav-burger { height:36px !important; align-items:center !important; padding:0 !important; margin-left:auto !important; }
        }
      `}</style>
    </>
  );
}

const styles = (scrolled) => ({
  nav: {
    position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
    background: scrolled ? 'rgba(10,10,10,0.97)' : 'transparent',
    borderBottom: scrolled ? '1px solid rgba(200,137,58,0.15)' : '1px solid transparent',
    backdropFilter: scrolled ? 'blur(20px)' : 'none',
    transition: 'all 0.4s ease',
  },
  inner: {
    maxWidth: 1280, margin: '0 auto', padding: '0 32px',
    height: 72, display: 'flex', alignItems: 'center', gap: 24,
  },
  logo: {
    display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', userSelect: 'none',
  },
  logoMark: {
    width: 42, height: 42, borderRadius: 10,
    background: 'linear-gradient(135deg, #c8893a, #e8a84a)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontFamily: "'Cormorant Garamond', serif", fontWeight: 700, fontSize: 17,
    color: '#0a0a0a', letterSpacing: 1, flexShrink: 0,
  },
  logoName: {
    fontFamily: "'Cormorant Garamond', serif", fontWeight: 600, fontSize: 18,
    color: '#f0ede6', lineHeight: 1.1,
  },
  logoSub: {
    fontSize: 9, color: '#c8893a', letterSpacing: 2.5, fontWeight: 600,
    marginTop: 1,
  },
  links: { gap: 4, marginLeft: 'auto' },
  link: (active) => ({
    background: 'none', border: 'none', cursor: 'pointer',
    padding: '8px 16px', borderRadius: 8, position: 'relative',
    fontFamily: "'Outfit', sans-serif", fontSize: 14, fontWeight: active ? 600 : 400,
    color: active ? '#c8893a' : 'rgba(240,237,230,0.7)',
    transition: 'all 0.2s', letterSpacing: 0.3,
  }),
  linkDot: {
    position: 'absolute', bottom: 2, left: '50%', transform: 'translateX(-50%)',
    width: 4, height: 4, borderRadius: '50%', background: '#c8893a', display: 'block',
  },
  cta: {
    background: 'linear-gradient(135deg, #c8893a, #e8a84a)',
    color: '#0a0a0a', border: 'none', borderRadius: 10,
    padding: '10px 22px', fontFamily: "'Outfit', sans-serif",
    fontWeight: 700, fontSize: 13.5, cursor: 'pointer',
    letterSpacing: 0.3, whiteSpace: 'nowrap',
    boxShadow: '0 4px 20px rgba(200,137,58,0.35)',
    transition: 'all 0.2s',
  },
  burger: {
    display: 'none', flexDirection: 'column', gap: 5, background: 'none', border: 'none',
    cursor: 'pointer', padding: 8,
  },
  burgerLine: (open, i) => ({
    width: 24, height: 2, background: '#f0ede6', borderRadius: 2,
    transition: 'all 0.3s ease', display: 'block',
    transform: open
      ? i === 0 ? 'translateY(7px) rotate(45deg)'
        : i === 1 ? 'scaleX(0)'
        : 'translateY(-7px) rotate(-45deg)'
      : 'none',
  }),
  mobileMenu: {
    background: 'rgba(10,10,10,0.98)', borderTop: '1px solid rgba(200,137,58,0.15)',
    padding: '16px 24px 24px', display: 'flex', flexDirection: 'column', gap: 4,
    animation: 'slideDown 0.25s ease',
  },
  mobileLink: {
    background: 'none', border: 'none', cursor: 'pointer',
    padding: '14px 12px', textAlign: 'left', borderBottom: '1px solid rgba(255,255,255,0.05)',
    fontFamily: "'Outfit', sans-serif", fontSize: 16, color: 'rgba(240,237,230,0.8)',
  },
  mobileCta: {
    marginTop: 12, background: 'linear-gradient(135deg,#c8893a,#e8a84a)',
    color: '#0a0a0a', border: 'none', borderRadius: 10, padding: '14px',
    fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: 15, cursor: 'pointer',
  },
});
